import { forwardRef } from 'react';
import './card.css';

const Card = forwardRef(({ 
  children, 
  className = '', 
  variant = 'default',
  interactive = true,
  onClick,
  href,
  target,
  rel,
  'aria-label': ariaLabel,
  ...props 
}, ref) => {
  const cardClasses = `card ${variant && `card--${variant}`} ${interactive && 'card--interactive'} ${className}`.trim();
  
  // If href is provided, render as a link
  if (href) {
    return (
      <a 
        ref={ref}
        href={href}
        target={target}
        rel={rel || (target === '_blank' ? 'noopener noreferrer' : undefined)}
        className={cardClasses}
        aria-label={ariaLabel}
        {...props}
      >
        {children}
      </a>
    );
  }
  
  // If onClick is provided, render as a button
  if (onClick) {
    return (
      <button 
        ref={ref}
        type="button"
        className={cardClasses}
        onClick={onClick}
        aria-label={ariaLabel}
        {...props}
      >
        {children}
      </button>
    );
  }
  
  // Default: render as article
  return (
    <article 
      ref={ref}
      className={cardClasses}
      aria-label={ariaLabel}
      {...props}
    >
      {children}
    </article>
  );
});

Card.displayName = 'Card';

// Card Header Component
export const CardHeader = ({ children, className = '', ...props }) => (
  <header className={`card__header ${className}`} {...props}>
    {children}
  </header>
);

// Card Body Component
export const CardBody = ({ children, className = '', ...props }) => (
  <div className={`card__body ${className}`} {...props}>
    {children}
  </div>
);

// Card Footer Component
export const CardFooter = ({ children, className = '', ...props }) => (
  <footer className={`card__footer ${className}`} {...props}>
    {children}
  </footer>
);

// Card Image Component
export const CardImage = ({ 
  src, 
  alt, 
  className = '', 
  loading = 'lazy',
  aspectRatio = '16/9',
  ...props 
}) => (
  <div className={`card__image-container ${className}`} style={{ aspectRatio }}>
    <img 
      src={src} 
      alt={alt} 
      className="card__image" 
      loading={loading}
      {...props}
    />
  </div>
);

// Card Icon Component
export const CardIcon = ({ children, className = '', variant = 'default', ...props }) => (
  <div className={`card__icon card__icon--${variant} ${className}`} {...props}>
    {children}
  </div>
);

// Card Title Component
export const CardTitle = ({ 
  children, 
  className = '', 
  as: Component = 'h3',
  ...props 
}) => (
  <Component className={`card__title ${className}`} {...props}>
    {children}
  </Component>
);

// Card Description Component
export const CardDescription = ({ children, className = '', ...props }) => (
  <p className={`card__description ${className}`} {...props}>
    {children}
  </p>
);

// Card Badge Component
export const CardBadge = ({ 
  children, 
  className = '', 
  variant = 'default',
  ...props 
}) => (
  <span className={`card__badge card__badge--${variant} ${className}`} {...props}>
    {children}
  </span>
);

export default Card;
